import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import {FocusTrap} from 'focus-trap-react';
import CloseButton from '../CloseButton';
import IfComponent from '../IfComponent';

const propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
  isVisible: PropTypes.bool,
  style: PropTypes.shape({}),
  bodyClassName: PropTypes.string,
  ariaLive: PropTypes.string,
  languageFocus: PropTypes.bool,
  contactPersonFocus: PropTypes.bool,
  contactOrderPersonFocus: PropTypes.bool,
  paused: PropTypes.bool,
};

function Dialog({
  className = '',
  children = '',
  onClose = () => {},
  isVisible = false,
  style = {},
  bodyClassName,
  ariaLive = 'polite',
  languageFocus = false,
  contactPersonFocus = false,
  contactOrderPersonFocus = false,
  paused = false,
}) {
  const trapNode = useRef(null);

  const onPause = () => {
    if (trapNode.current) {
      trapNode.current.focusTrap.pause();
    }
  };

  useEffect(() => {
    const handleDocumentClick = event => {
      if (trapNode.current && !trapNode.current.contains(event.target)) {
        const customEvent = new CustomEvent('customMousedown');
        document.dispatchEvent(customEvent);

        // Delay deactivation of FocusTrap to allow the button's onClick to trigger
        setTimeout(() => {
          if (trapNode.current) {
            trapNode.current.focusTrap.deactivate();
          }
        }, 0);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleDocumentClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isVisible]);

  const setTrapNode = useCallback(node => {
    if (node) {
      trapNode.current = node.node; // Access the DOM node from the FocusTrap ref
    }
  }, []);

  useEffect(() => {
    if (contactPersonFocus || contactOrderPersonFocus || languageFocus) {
      onPause();
    }
  }, [contactPersonFocus, contactOrderPersonFocus, languageFocus]);

  return (
    <IfComponent
      condition={isVisible}
      whenTrue={
        <FocusTrap
          focusTrapOptions={{
            escapeDeactivates: false,
            allowOutsideClick: true,
            preventScroll: true,
            clickOutsideDeactivates: true,
          }}
          ref={setTrapNode}
          paused={paused}
        >
          <div
            className={`tk-dialog__wrapper ${className}`}
            style={{
              left: style.left,
              top: style.top,
            }}
            aria-live={ariaLive}
          >
            <span
              className="tk-dialog__wrapper--before"
              style={{
                borderRightColor: style.borderRightColor,
                borderLeftColor: style.borderLeftColor,
                borderBottomColor: style.borderBottomColor,
                borderTopColor: style.borderTopColor,
                transform: style.tipTransform,
                right: style.tipRight,
                top: style.tipTop,
              }}
            />
            <CloseButton
              onClick={onClose}
              className="tk-dialog__btn-close tv-buttons__type icon-close"
            />
            <div className={`tk-dialog__body ${bodyClassName}`}>{children}</div>
          </div>
        </FocusTrap>
      }
      whenFalse={null}
    />
  );
}

Dialog.propTypes = propTypes;

export default Dialog;
